import './ComposedChart.scss';
import * as R from 'ramda';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Area, CartesianGrid, ComposedChart as AC, Line, Tooltip, XAxis, YAxis } from 'recharts';
import { CompanyFiscalCal } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { getColorMap, Neutral200 } from '../../../utils/colors';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { CARTESIAN_GRID_STROKE, CARTESIAN_GRID_STROKE_WIDTH, TICK_STYLE } from '../ChartConstants';
import { ChartTooltip } from '../ChartTooltip/ChartTooltip';
import { headerFormatter, useDynamicYAxisWidth } from '../ChartUtils';
import { DefaultLegend } from '../Legend/DefaultLegend/DefaultLegend';
import { LegendWithGrouping } from '../Legend/LegendWithGrouping';

const INACTIVE_COLOR = Neutral200;

interface ComposedChartTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: any[];
  dateGrouping?: string;
  fiscalCal: CompanyFiscalCal;
  tooltipFormatter?: (value: any) => string;
  tooltipShape?: 'circle' | 'square' | 'line';
  reverseToolTipItems?: boolean;
}

const ComposedChartTooltip = ({
  active,
  label,
  payload,
  dateGrouping,
  fiscalCal,
  tooltipFormatter,
  tooltipShape,
  reverseToolTipItems
}: ComposedChartTooltipProps) => {
  if (!active || !payload || !label || !dateGrouping) return null;

  const header = headerFormatter(dateGrouping, String(label), fiscalCal);
  const items = R.sortBy(
    (item: any) => -Math.abs(item.value),
    payload.map(item => ({
      label: item.name as string,
      value: tooltipFormatter ? tooltipFormatter(item.value) : (item.value as number),
      color: item.stroke
    }))
  );

  return (
    <ChartTooltip
      headerLabel={header}
      items={items}
      shape={tooltipShape}
      reverseItems={reverseToolTipItems}
    />
  );
};

interface ComposedChartProps {
  areas: {
    name: string;
    dataKey: string;
    grouping?: { channel: string; platform: string; tactic: string; brand_nonbrand: string };
    incrementality?: boolean;
    toolTip?: string;
    line?: boolean;
  }[];
  data: any[];
  dateGrouping?: string;
  colorOverrides?: Record<string, string>;
  hardCodedColor?: string;
  hardCodedColorName?: string;
  hideLegend?: boolean;
  groupedAreas?: any[];
  incrementality?: boolean;
  syncId?: number;
  tooltipFormatter?: (value: any) => string;
  tooltipShape?: 'circle' | 'square' | 'line';
  reverseToolTipItems?: boolean;
  reverseLegend?: boolean;
  usePercentageYAxis?: boolean;
  xAxisDataKey: string;
  xAxisTickFormatter?: (value: any) => string;
  yAxisDataKey?: string;
  areaYAxisTickFormatter?: (value: any) => string;
  lineYAxisTickFormatter?: (value: any) => string;
  areaYAxisOrientation?: 'left' | 'right';
  lineYAxisOrientation?: 'left' | 'right';
}

export const ComposedChart = ({
  areas,
  data,
  dateGrouping,
  colorOverrides,
  hardCodedColor,
  hardCodedColorName,
  hideLegend,
  groupedAreas,
  incrementality,
  syncId,
  tooltipFormatter,
  tooltipShape,
  reverseToolTipItems,
  reverseLegend,
  usePercentageYAxis = false,
  xAxisDataKey,
  xAxisTickFormatter,
  yAxisDataKey,
  areaYAxisTickFormatter,
  lineYAxisTickFormatter,
  areaYAxisOrientation = 'left',
  lineYAxisOrientation = 'left'
}: ComposedChartProps) => {
  const [activeArea, setActiveArea] = useState<string | null>(null);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [hoveredLegend, setHoveredLegend] = useState<string | null>(null);
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  const { xAxisHeight, yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 3
  });

  const colorMap = useMemo(() => {
    if (R.isEmpty(areas)) return undefined;
    const colorsOriginal = getColorMap(areas, hardCodedColorName, hardCodedColor);
    return { ...colorsOriginal, ...colorOverrides };
  }, [areas, hardCodedColorName, hardCodedColor, colorOverrides]);

  const handleLegendClick = useCallback(
    (line: string, groupChannel?: string) => {
      if (activeArea === line) {
        setActiveArea(null);
        setActiveGroup(null);
      } else {
        setActiveArea(line);
        if (groupChannel) {
          setActiveGroup(groupChannel);
        }
      }
    },
    [activeArea]
  );

  const handleLegendEnter = useCallback(
    line => {
      if (hoveredLegend === line) {
        setHoveredLegend(null);
      } else {
        setHoveredLegend(line);
      }
    },
    [hoveredLegend]
  );

  const composedChartAreas: ReactNode = useMemo(
    () => (
      <>
        {areas.map((area, i) => {
          const isActiveArea = activeArea === null ? true : activeArea === area.name;
          const isHoveredLegend = hoveredLegend === null ? true : hoveredLegend === area.name;
          if (!isActiveArea && activeArea !== null) {
            return null;
          }

          const activeColor = isActiveArea && colorMap ? colorMap[area.name] : INACTIVE_COLOR;
          const hoverColor = isHoveredLegend && colorMap ? colorMap[area.name] : INACTIVE_COLOR;
          const isArea = !area.line;
          return isArea ? (
            <Area
              activeDot={false}
              dataKey={area.dataKey}
              fill={activeArea ? activeColor : hoverColor}
              fillOpacity={1}
              isAnimationActive={false}
              key={i}
              name={area.name}
              stackId='1'
              stroke={activeArea ? activeColor : hoverColor}
              strokeWidth={0}
              type='monotone'
              yAxisId={areaYAxisOrientation}
            />
          ) : (
            <Line
              yAxisId={lineYAxisOrientation}
              activeDot={false}
              dot={false}
              dataKey={area.dataKey}
              fill={activeArea ? activeColor : hoverColor}
              fillOpacity={1}
              isAnimationActive={false}
              key={i}
              name={area.name}
              stroke={activeArea ? activeColor : hoverColor}
              strokeWidth={4}
              type='monotone'
            />
          );
        })}
      </>
    ),
    [areaYAxisOrientation, colorMap, lineYAxisOrientation, areas, activeArea, hoveredLegend]
  );

  const percentageYAxis = (
    <YAxis
      dataKey={yAxisDataKey}
      domain={[0, 100]}
      orientation='left'
      tick={TICK_STYLE}
      ticks={[0, 25, 50, 75, 100]}
      type='number'
      yAxisId='0'
      unit='%'
    />
  );

  return (
    <div className='composed-chart' style={{ display: 'flex', flex: 1 }}>
      <div style={{ display: 'flex', flex: 1 }}>
        <AutoSizer
          renderProp={({ width = 0, height = 0 }) => (
            <AC data={data} height={height} syncId={syncId} ref={setChartRef} width={width}>
              <CartesianGrid
                stroke={CARTESIAN_GRID_STROKE}
                strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                vertical={false}
              />
              <XAxis
                axisLine={false}
                dataKey={xAxisDataKey}
                height={xAxisHeight}
                tick={TICK_STYLE}
                tickFormatter={xAxisTickFormatter}
              />
              {usePercentageYAxis && percentageYAxis}
              {!usePercentageYAxis && (
                <YAxis
                  axisLine={false}
                  dataKey={yAxisDataKey}
                  tick={TICK_STYLE}
                  tickFormatter={areaYAxisTickFormatter}
                  tickLine={false}
                  width={yAxisWidth}
                  yAxisId={areaYAxisOrientation}
                  orientation={areaYAxisOrientation}
                />
              )}
              <YAxis
                axisLine={false}
                dataKey={yAxisDataKey}
                tick={TICK_STYLE}
                tickFormatter={lineYAxisTickFormatter}
                tickLine={false}
                width={yAxisWidth}
                yAxisId={lineYAxisOrientation}
                orientation={lineYAxisOrientation}
              />
              {composedChartAreas}
              <Tooltip
                content={
                  <ComposedChartTooltip
                    dateGrouping={dateGrouping}
                    fiscalCal={fiscalCal}
                    tooltipFormatter={tooltipFormatter}
                    tooltipShape={tooltipShape}
                    reverseToolTipItems={reverseToolTipItems}
                  />
                }
                isAnimationActive={false}
              />
            </AC>
          )}
        />
      </div>
      <div className={`right-of-chart ${hideLegend ? 'hide' : ''}`}>
        {(!groupedAreas || R.isEmpty(groupedAreas)) && (
          <DefaultLegend
            areas={areas}
            activeArea={activeArea}
            handleLegendClick={handleLegendClick}
            incrementality={incrementality}
            setHoveredLegend={setHoveredLegend}
            handleLegendEnter={handleLegendEnter}
            hoveredLegend={hoveredLegend}
            colorMap={colorMap}
            reverseLegend={reverseLegend}
          />
        )}
        {groupedAreas && !R.isEmpty(groupedAreas) && (
          <LegendWithGrouping
            activeArea={activeArea}
            activeGroup={activeGroup}
            handleLegendClick={handleLegendClick}
            setHoveredLegend={setHoveredLegend}
            handleLegendEnter={handleLegendEnter}
            hoveredLegend={hoveredLegend}
            incrementality={incrementality}
            colorMap={colorMap}
            groupedAreas={groupedAreas}
          />
        )}
      </div>
    </div>
  );
};
